import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { GQLProductSideAvailableDecorationType, GQLProductSideFragment } from '@theprintbar/graphql';
import {
	ImageSize,
	cn,
	getDecorationContent,
	getMaxDecorationTypeColours,
	getSamedayName,
	getSortedDecorationTypes,
	sizedImage,
} from '@theprintbar/shared';
import { useCallback, useEffect, useState } from 'react';
import { ButtonSize, LightningBolt, NumberInput, OutlineButton, PrimaryButton, RichText, UploadArea } from '../../..';
import ConfirmModal, { useConfirmModal } from '../../ConfirmModal/ConfirmModal';
import { SidePreviewProps } from './SidePreview';
import { DecorationAttachment } from './decorationAttachment';

type ChangeDecorationMethodProps = SidePreviewProps & {
	side: GQLProductSideFragment;
	temporaryDecoration: GQLProductSideAvailableDecorationType | null;
	temporaryColourCount: number | null;
	setTemporaryDecoration: (type: GQLProductSideAvailableDecorationType) => void;
	setTemporaryColourCount: (count: number) => void;
	childRef: React.RefObject<HTMLDivElement>;
};

export function ChangeDecorationMethod({
	product,
	operationalSettings,
	activeSide,
	activeDecoration,
	merch,
	handleTypeChange,
	handleColourChange,
	store,
	side,
	temporaryDecoration,
	temporaryColourCount,
	setTemporaryDecoration,
	setTemporaryColourCount,
	childRef,
	activePanel,
	setActivePanel,
	content,
}: ChangeDecorationMethodProps) {
	const [selectedDecoration, setSelectedDecoration] = useState<ReturnType<typeof getDecorationContent> | null>(null);
	const [colourCountError, setColourCountError] = useState<string | null>(null);
	const [maxTemporaryColours, setMaxTemporaryColours] = useState(
		getMaxDecorationTypeColours(temporaryDecoration || activeDecoration?.type, product),
	);
	const handleTempDecorationChange = useCallback(
		async (type: GQLProductSideAvailableDecorationType) => {
			if (merch?.sameday && type !== GQLProductSideAvailableDecorationType.Dtg) {
				const samedayName = getSamedayName();
				setConfirmContent(
					<div className="mb-4">
						<h5 className="mb-2 uppercase">
							{getDecorationContent(type, operationalSettings, product).title} unavailable {samedayName}
						</h5>
						<p>
							In order to select {getDecorationContent(type, operationalSettings, product).title}, {samedayName} will be
							turned off.
						</p>
					</div>,
				);
				const result = await confirm();
				if (!result) {
					setActivePanel('main');
					return;
				}
			}
			setTemporaryDecoration(type);
			const newMaxColours = getMaxDecorationTypeColours(type, product);
			setMaxTemporaryColours(newMaxColours);
			setTemporaryColourCount(Math.min(newMaxColours, temporaryColourCount || 0));
			setSelectedDecoration(getDecorationContent(type, operationalSettings, product));
			if ([GQLProductSideAvailableDecorationType.Emb, GQLProductSideAvailableDecorationType.Scr].includes(type)) {
				setActivePanel('colour');
			} else {
				setActivePanel('main');
				handleTypeChange(type, side.availableDecorations.includes(type), undefined);
			}
		},
		[temporaryDecoration, temporaryColourCount, activeDecoration, product],
	);

	const handleChangingDecoration = useCallback(() => {
		if (!temporaryDecoration || !merch) return;
		let available = side.availableDecorations.includes(temporaryDecoration);

		if (!store && merch.sameday && temporaryDecoration !== GQLProductSideAvailableDecorationType.Dtg) {
			available = false;
		}
		handleTypeChange(temporaryDecoration, available, temporaryColourCount || undefined);
	}, [temporaryDecoration, merch, temporaryColourCount, side]);
	const [prevPanel, setPrevPanel] = useState<'main' | 'method' | 'colour'>('main');
	const [methodPanelAnim, setMethodPanelAnim] = useState('translate-x-full');

	useEffect(() => {
		setPrevPanel(prev => {
			if (activePanel === 'method') {
				setTimeout(() => setMethodPanelAnim('translate-x-0'), 10);
			} else if (prev === 'method') {
				if (activePanel === 'colour') {
					setMethodPanelAnim('-translate-x-full opacity-0'); // exit left
				} else {
					setMethodPanelAnim('translate-x-full opacity-0'); // exit right
				}
			} else {
				setMethodPanelAnim('translate-x-full opacity-0');
			}
			return activePanel;
		});
	}, [activePanel]);

	const methodPanelClass = cn('absolute top-0 left-0 p-4 w-full h-full transition duration-500 ease-in-out', methodPanelAnim);

	const colourPanelClass = cn(
		'absolute top-0 left-0 p-4 w-full h-full transition duration-500 ease-in-out',
		activePanel === 'colour' ? 'translate-x-0' : 'translate-x-full opacity-0',
	);

	const [confirmContent, setConfirmContent] = useState<React.ReactNode>(null);
	const { confirm, ConfirmModal } = useConfirmModal({
		content: confirmContent,
		className: 'max-w-sm min-h-42',
		actionContainerClassName: 'mt-auto',
		actions: [
			{
				ActionButton: onClick => (
					<PrimaryButton key="ok" onClick={onClick}>
						Confirm
					</PrimaryButton>
				),
				onClick: () => true,
			},
			{
				ActionButton: onClick => (
					<OutlineButton key="cancel" onClick={onClick}>
						Cancel
					</OutlineButton>
				),
				onClick: () => false,
			},
		],
	});

	return (
		<>
			<ConfirmModal />
			<div className={methodPanelClass}>
				<h5 className="mb-2 uppercase">Available Decoration Methods</h5>
				<div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
					{getSortedDecorationTypes(side).map(type => {
						let { title, text, icon, bgColour } = getDecorationContent(type, operationalSettings);
						let className = cn(
							'w-full relative border bg-opacity-20 hover:not(:disabled):bg-opacity-40 rounded-3xl px-4 py-2 flex items-center gap-2 disabled:opacity-40 disabled:text-grey border-grey hover:not(:disabled):border-blue',
							bgColour,
							temporaryDecoration === type && 'border-blue ring ring-blue/20 ring-1 ring-offset-2',
						);

						return (
							<button key={type} className={className} onClick={() => handleTempDecorationChange(type)}>
								{icon && (
									<img
										loading="lazy"
										className="my-auto w-6 h-6"
										src={sizedImage(icon.image.url, ImageSize.Tiny)}
										alt={icon.altText || `${type} icon`}
									/>
								)}
								<div className="text-left">
									<p className="flex items-center text-base">
										{title}
										{type === GQLProductSideAvailableDecorationType.Dtg && (
											<LightningBolt className="w-3 h-3 lg:w-4 lg:h-4 ml-1" />
										)}
									</p>
									<h6 className="text-xs">{text}</h6>
								</div>
							</button>
						);
					})}
				</div>
			</div>
			<div className={colourPanelClass}>
				<div className="flex justify-between items-center mb-2"></div>
				<div className="flex flex-col justify-between gap-2">
					<div className="flex w-full justify-between gap-10">
						<div className="">
							<h5 className="mb-2 uppercase">
								Select your{' '}
								{temporaryDecoration === GQLProductSideAvailableDecorationType.Scr ? 'colours/screens' : 'threads'}
							</h5>
							<RichText
								content={
									temporaryDecoration === GQLProductSideAvailableDecorationType.Scr
										? content.screenColourRichText
										: content.embroideryThreadRichText
								}
							/>
						</div>
					</div>
					<div className="w-full">
						<div>
							<div className="flex md:justify-between md:items-center gap-4 mt-2">
								<OutlineButton
									className="hidden md:flex flex-row-reverse gap-2 text-grey border-grey"
									onClick={() => setActivePanel('method')}
									icon={ArrowLeftIcon}
									iconClass="-ml-1"
									size={ButtonSize.S}
								>
									Return to selection
								</OutlineButton>
								<div className="flex flex-col w-full md:w-min md:flex-row gap-2">
									{!!temporaryDecoration &&
									[GQLProductSideAvailableDecorationType.Emb, GQLProductSideAvailableDecorationType.Scr].includes(
										temporaryDecoration,
									) ? (
										<div className={cn('flex w-full md:w-20 transition border border-blue rounded-full')}>
											<NumberInput
												hideLabel
												key={maxTemporaryColours}
												name={`${activeSide}.tempColours`}
												outsideForm
												noValidate
												value={temporaryColourCount || 0}
												min={0}
												max={maxTemporaryColours}
												handleUpdate={setTemporaryColourCount}
												className="w-full md:w-20 h-full py-0.5"
											/>
										</div>
									) : (
										<p>Unlimited</p>
									)}

									<PrimaryButton
										disabled={!temporaryDecoration}
										onClick={() => {
											if (!temporaryDecoration || temporaryColourCount === 0) {
												if (temporaryColourCount === 0) {
													setColourCountError('Please select at least one colour.');
												}
												return;
											}
											setColourCountError(null);
											handleTypeChange(
												temporaryDecoration,
												side.availableDecorations.includes(temporaryDecoration),
												temporaryColourCount || undefined,
											);
											setActivePanel('main');
										}}
										size={ButtonSize.S}
										className="uppercase font-belfast !font-bold text-xs px-2 lg:px-2.5"
									>
										Confirm Decoration
									</PrimaryButton>
									<OutlineButton
										className="md:hidden flex-row-reverse gap-2 text-grey border-grey"
										onClick={() => setActivePanel('method')}
										icon={ArrowLeftIcon}
										iconClass="-ml-1"
										size={ButtonSize.S}
									>
										Return to selection
									</OutlineButton>
								</div>
							</div>
							{colourCountError && <p className="text-red-500 text-xs text-right mt-1">{colourCountError}</p>}
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
